import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { MainLayout } from './components/layout/MainLayout';
import Login from './pages/Login';
import NotFound from './pages/NotFound';

// Lazy load pages for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Bookings = React.lazy(() => import('./pages/Bookings'));
const AssignmentBoard = React.lazy(() => import('./pages/AssignmentBoard'));
const Configuration = React.lazy(() => import('./pages/Configuration'));
const Fleet = React.lazy(() => import('./pages/Fleet'));
const Finance = React.lazy(() => import('./pages/Finance'));


// Loading fallback component
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// App Router Component (uses hooks, so must be inside providers)
const AppRouter: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          user ? <Navigate to="/dashboard" replace /> : <Login />
        } 
      />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <React.Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Dashboard */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />

                  {/* Core Operations */}
                  <Route path="/bookings" element={<Bookings />} />
                  <Route path="/assignments" element={<AssignmentBoard />} />

                  {/* Configuration */}
                  <Route path="/configuration" element={<Configuration />} />

                  {/* Fleet Management */}
                  <Route path="/fleet" element={<Fleet />} />

                  {/* Financial */}
                  <Route path="/finance" element={<Finance />} />

                  {/* Advanced Features 
                  <Route path="/zones" element={<Zones />} />
                  <Route path="/promotions" element={<Promotions />} />
                  <Route path="/templates" element={<Templates />} />
                  */}
                  {/* System 
                  <Route path="/audit" element={<AuditLogs />} />
                  */}

                  {/* Catch all - 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </React.Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// Main App Component (all providers are in index.tsx)
const App: React.FC = () => {
  return (
    <div className="App">
      <AppRouter />
    </div>
  );
};

export default App;